// @pages/account/PasswordResetRequest.page.tsx

import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { renderRoute, routeLogin } from "@navigation/typedRoutes";
import { useAuthenticationClient } from "@contexts/AuthenticationClient.context";
import { useLoadingController } from "@contexts/Loading.context";
import { useIoOptionState } from "@utils/useIoOptionState";
import { useIoState } from "@utils/useIoState";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import PasswordResetRequestView from "@view/authentication/PasswordResetRequest.view";

import { ok, type Ok } from "@ps-bridge/ok";
import { mkNavigate, mkTaskRunner } from "@ps-bridge/capabilities";
import type { PasswordResetError, PasswordResetStartRequest } from "@ps-bridge/models/core/dataContracts";
import type { TaskEitherTs, TaskTs } from "@ps-bridge/interop";
import { ioEffect, taskEitherFromEffect } from '@utils/effects/branded';
import * as PSOption from "@utils/Option";
import type { PasswordResetRequestKernel } from "@ps-bridge/contracts/passwordResetRequest";
import { mkPasswordResetRequestController } from "@ps-bridge/controllers/passwordResetRequest";
import { buildPasswordResetRequestViewProps } from "@ps-bridge/passwordResetRequest.program";

const PasswordResetRequestPage: React.FC = () => {
    const navigate = useNavigate();
    const authenticationClient = useAuthenticationClient();
    const loading = useLoadingController();
    const emailState = useIoOptionState<string>(PSOption.none());
    const submittedState = useIoState<boolean>(false);
    const runTaskWithError = useRunTaskWithError();
    const runTaskWithErrorIo = useCallback(
        (task: TaskTs<Ok>) =>
            ioEffect(() => {
                runTaskWithError(task).then(() => ok);
                return ok;
            }),
        [runTaskWithError],
    );
    const requestPasswordReset = useMemo(
        () =>
            (request: PasswordResetStartRequest): TaskEitherTs<PasswordResetError, Ok> =>
                taskEitherFromEffect(
                    authenticationClient.requestPasswordReset(request),
                    (error) => error as PasswordResetError,
                ),
        [authenticationClient],
    );
    const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);
    const controller = useMemo(
        () =>
            mkPasswordResetRequestController({
                email: {
                    read: emailState.read,
                    setSome: emailState.setSome,
                    setMaybe: emailState.setMaybe,
                },
                submitted: {
                    read: submittedState.read,
                    set: submittedState.set,
                },
                loading,
                navigate: mkNavigate((route) =>
                    ioEffect(() => {
                        navigate(renderRoute(route));
                        return ok;
                    }),
                ),
                loginRoute: routeLogin,
                requestPasswordReset,
                taskRunner,
            }),
        [
            emailState.read,
            emailState.setMaybe,
            emailState.setSome,
            loading,
            requestPasswordReset,
            submittedState.read,
            submittedState.set,
            taskRunner,
            navigate,
        ],
    );
    const kernel: PasswordResetRequestKernel = useMemo(
        () => ({
            controller,
            loading,
            email: {
                read: emailState.read,
                setSome: emailState.setSome,
                setMaybe: emailState.setMaybe,
            },
            submitted: {
                read: submittedState.read,
                set: submittedState.set,
            },
        }),
        [
            controller,
            emailState.read,
            emailState.setMaybe,
            emailState.setSome,
            loading,
            submittedState.read,
            submittedState.set,
        ],
    );
    const viewProps = useMemo(
        () =>
            buildPasswordResetRequestViewProps({
                kernel,
                logoUri: PSOption.some("/assets/ouroboros_logo_flat.png"),
            }),
        [kernel],
    );

    return (
        <PasswordResetRequestView {...viewProps} />
    );
};

export default PasswordResetRequestPage;
